/*-------------------------------------------*/
/*----------IMPORTS AND INITIALIZERS---------*/
/*-------------------------------------------*/

// Imports
import { useNavigate } from 'react-router-dom'
import { 
    Article, Book, FolderCopy, 
    Help, Lock, NotificationsNoneOutlined, 
    PersonOutline, PrivacyTip, Settings 
} from "@mui/icons-material";
import { 
    Box, Divider, Grid, IconButton, 
    List, ListItem, ListItemButton, 
    ListItemIcon, ListItemText, Stack, Tooltip, 
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import { useEffect, useState } from 'react';

/*-------------------------------------------*/
/*------------PRE-CONFIG INTERFACES----------*/
/*-------------------------------------------*/

// Part of type definition for props
interface Props {
    drawerWidth: number,
    title: string,
    open: boolean
}

/*------------------------------------------*/
/*-----------------COMPONENT----------------*/
/*------------------------------------------*/

const SideBar: React.FC<Props> = ({
    drawerWidth, title, open
}) => {

    /*--------------------*/
    /*-STATES AND METHODS-*/
    /*--------------------*/
    
    // For navigating between pages
    const navigate = useNavigate()

    // For rendering icons
    const getIcon = (text: string) => {
        switch (text) {
          case 'Dashboard':
            return <FolderCopy />
          case 'Accounts':
            return <Article />
          case 'Reports':
            return <Book />
          case 'Settings':
            return <Settings />
          case 'Help':
            return <Help />
          default:
            return null
        }
    }

    // Screen size
    const theme = useTheme();

    const isSmallorLarger = useMediaQuery(theme.breakpoints.up('sm'));
    const [isViewportSmallOrLarger, setIsViewportSmallOrLarger] = useState(isSmallorLarger);

    useEffect(() => {
      setIsViewportSmallOrLarger(isSmallorLarger);
    }, [isSmallorLarger]);

    /*------------------------------------------*/
    /*-----------------COMPONENT----------------*/
    /*------------------------------------------*/

    return (
        <Box
            component="nav"
            sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 }}}
            aria-label="mailbox folders"
        >

            <Box 
                style={{
                    minHeight: '92vh', 
                    display: 'flex', 
                    flexDirection: 'column'
                }}
            >

                <Divider sx={{bgcolor: 'white'}}/>

                {/* Render sidebar (drawer) list items */}
                <List>
                    <Stack spacing={1} direction='column'>
                    {
                        ['Accounts'/*, 'Dashboard', 'Reports', 'Settings', 'Help'*/].map((text, index) => (
                            <ListItem 
                                key={text} 
                                disablePadding 
                                sx={{ 
                                    display: 'block', 
                                    '&:hover': { bgcolor: '#2d2c47' },
                                    bgcolor: title === text ? '#2d2c47' : '#1e1d31',
                                    borderRight: title === text ? '4px solid #ff4e3c' : 'none'
                                }}
                            >
                                
                                    <ListItemButton
                                        sx={{ minHeight: 48, justifyContent: 1, px: 2.5 }}
                                        onClick={() => navigate(`/${text}`)}
                                    >
                                        <Tooltip 
                                            title={text} 
                                            placement="right-start" 
                                        >
                                            <ListItemIcon
                                                sx={{
                                                    minWidth: 0,
                                                    mr: 3,
                                                    justifyContent: 'center',
                                                    color: '#b3b3b3'
                                                }}
                                            >
                                                {getIcon(text)}
                                            </ListItemIcon>
                                        </Tooltip>
                                        <ListItemText primary={text} sx={{ color: '#b3b3b3' }}/>
                                    </ListItemButton>

                            </ListItem>
                        ))
                    }
                    </Stack>
                </List>

                {/* Render sidebar (drawer) footer */}
                <Box
                    component="footer"
                    sx={{ marginTop: 'auto'}}
                >
                    
                    <List>
                        <Stack spacing={1} direction='column'>
                            <Divider sx={{bgcolor: 'white'}}/>
                            <ListItem 
                                disablePadding 
                                sx={{  
                                    bgcolor: '#1e1d31',
                                    height: '20px',
                                    display: 'block'
                                }}
                            >
                                <ListItemButton
                                    onClick={() => window.open(`${process.env.REACT_APP_MOVIUS_WEBSITE}`, '_blank')}
                                >
                                    {
                                        open ?
                                        <Typography sx={{color: '#b3b3b3', pr: '18px'}}> {process.env.REACT_APP_COPYRIGHT} </Typography>
                                        : <Typography sx={{color: '#b3b3b3', pr: '18px'}}> © </Typography>
                                    }
                                </ListItemButton>
                            </ListItem>
                        </Stack>
                    </List>
                </Box>

            </Box>
        </Box>
    );
}
 
export default SideBar;